import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  TransactionBankOperationCategory,
  TransactionCategory,
  TransactionCreditCategory,
  TransactionExpenseCategory,
  TransactionIncomeCategory,
  TransactionRefundCategory,
  TransactionSavingCategory,
  TransactionTaxCategory,
} from '@algoan/dashboard/model';
import { monitoring } from '@algoan/dashboard/utils';

export type TransactionCategoryTranslator = (category: TransactionCategory | undefined) => string;
const loggedUnknownTransactionCategory: Array<TransactionCategory | undefined> = [];
const logUnknownTransactionCategoryToDatadogRum = (category: TransactionCategory | undefined): void => {
  if (loggedUnknownTransactionCategory.includes(category)) {
    return;
  }

  monitoring.logError({
    fingerprint: 'unknown_transaction_category',
    message: 'Unknown category for transaction',
    context: { category: category },
  });

  loggedUnknownTransactionCategory.push(category);
};

export const useTransactionCategoryTranslator = (): TransactionCategoryTranslator => {
  const intl = useIntl();
  return useCallback<TransactionCategoryTranslator>(
    (category) => {
      if (!category || !(category in transactionCategoryMessages)) {
        logUnknownTransactionCategoryToDatadogRum(category);

        return intl.formatMessage(transactionCategoryMessages.notDetermined);
      }

      return intl.formatMessage(transactionCategoryMessages[category]);
    },
    [intl],
  );
};

const transactionCategoryMessages = defineMessages({
  notDetermined: {
    id: 'transaction.category.not_determined',
    defaultMessage: 'Not determined',
    description: 'Label for unknown transaction category',
  },
  [TransactionTaxCategory.HOUSING_TAX]: {
    id: 'transaction.category.HOUSING_TAX',
    defaultMessage: 'Housing tax',
    description: 'Label of transaction category HOUSING_TAX',
  },
  [TransactionTaxCategory.INCOME_TAX]: {
    id: 'transaction.category.INCOME_TAX',
    defaultMessage: 'Income tax',
    description: 'Label of transaction category INCOME_TAX',
  },
  [TransactionTaxCategory.PERSO_TAX]: {
    id: 'transaction.category.PERSO_TAX',
    defaultMessage: 'Personal taxes',
    description: 'Label of transaction category PERSO_TAX',
  },
  [TransactionTaxCategory.PERSO_TAX_CREDIT]: {
    id: 'transaction.category.PERSO_TAX_CREDIT',
    defaultMessage: 'Personal tax credit',
    description: 'Label of transaction category PERSO_TAX_CREDIT',
  },
  [TransactionTaxCategory.PERSO_TAX_REFUND]: {
    id: 'transaction.category.PERSO_TAX_REFUND',
    defaultMessage: 'Personal tax refund',
    description: 'Label of transaction category PERSO_TAX_REFUND',
  },
  [TransactionTaxCategory.PROPERTY_TAX]: {
    id: 'transaction.category.PROPERTY_TAX',
    defaultMessage: 'Property tax',
    description: 'Label of transaction category PROPERTY_TAX',
  },
  [TransactionTaxCategory.PRO_TAX]: {
    id: 'transaction.category.PRO_TAX',
    defaultMessage: 'Professional tax',
    description: 'Label of transaction category PRO_TAX',
  },
  [TransactionTaxCategory.PRO_TAX_REFUND]: {
    id: 'transaction.category.PRO_TAX_REFUND',
    defaultMessage: 'Professional tax refund',
    description: 'Label of transaction category PRO_TAX_REFUND',
  },
  [TransactionBankOperationCategory.FEES_ACCOUNT_SEIZURE]: {
    id: 'transaction.category.FEES_ACCOUNT_SEIZURE',
    defaultMessage: 'Account seizure fees',
    description: 'Label of transaction category FEES_ACCOUNT_SEIZURE',
  },
  [TransactionBankOperationCategory.FEES_BANK_SERVICE]: {
    id: 'transaction.category.FEES_BANK_SERVICE',
    defaultMessage: 'Bank service fees',
    description: 'Label of transaction category FEES_BANK_SERVICE',
  },
  [TransactionBankOperationCategory.FEES_CHECK_REJECTION]: {
    id: 'transaction.category.FEES_CHECK_REJECTION',
    defaultMessage: 'Check rejection fees',
    description: 'Label of transaction category FEES_CHECK_REJECTION',
  },
  [TransactionBankOperationCategory.FEES_DIRECT_DEBT_RECOVERY]: {
    id: 'transaction.category.FEES_DIRECT_DEBT_RECOVERY',
    defaultMessage: 'Direct debt recovery fees',
    description: 'Label of transaction category FEES_DIRECT_DEBT_RECOVERY',
  },
  [TransactionBankOperationCategory.FEES_INCIDENT]: {
    id: 'transaction.category.FEES_INCIDENT',
    defaultMessage: 'Incident fees',
    description: 'Label of transaction category FEES_INCIDENT',
  },
  [TransactionBankOperationCategory.FEES_INTERVENTION]: {
    id: 'transaction.category.FEES_INTERVENTION',
    defaultMessage: 'Intervention fees',
    description: 'Label of transaction category FEES_INTERVENTION',
  },
  [TransactionBankOperationCategory.FEES_OVERDRAFT]: {
    id: 'transaction.category.FEES_OVERDRAFT',
    defaultMessage: 'Overdraft fees',
    description: 'Label of transaction category FEES_OVERDRAFT',
  },
  [TransactionBankOperationCategory.FEES_UNAUTHORIZED_OVERDRAFT]: {
    id: 'transaction.category.FEES_UNAUTHORIZED_OVERDRAFT',
    defaultMessage: 'Unauthorized overdraft fees',
    description: 'Label of transaction category FEES_UNAUTHORIZED_OVERDRAFT',
  },
  [TransactionBankOperationCategory.FEES_PAYMENT_REJECTION]: {
    id: 'transaction.category.FEES_PAYMENT_REJECTION',
    defaultMessage: 'Payment rejection fees',
    description: 'Label of transaction category FEES_PAYMENT_REJECTION',
  },
  [TransactionBankOperationCategory.FEES_PREVENTIVE_SEIZURE]: {
    id: 'transaction.category.FEES_PREVENTIVE_SEIZURE',
    defaultMessage: 'Preventive seizure fees',
    description: 'Label of transaction category FEES_PREVENTIVE_SEIZURE',
  },
  [TransactionBankOperationCategory.PROVISION_ACCOUNT_SEIZURE]: {
    id: 'transaction.category.PROVISION_ACCOUNT_SEIZURE',
    defaultMessage: 'Provision account seizure',
    description: 'Label of transaction category PROVISION_ACCOUNT_SEIZURE',
  },
  [TransactionBankOperationCategory.PROVISION_DIRECT_DEBT_RECOVERY]: {
    id: 'transaction.category.PROVISION_DIRECT_DEBT_RECOVERY',
    defaultMessage: 'Provision direct debt recovery',
    description: 'Label of transaction category PROVISION_DIRECT_DEBT_RECOVERY',
  },
  [TransactionBankOperationCategory.PROVISION_PREVENTIVE_SEIZURE]: {
    id: 'transaction.category.PROVISION_PREVENTIVE_SEIZURE',
    defaultMessage: 'Provision preventive seizure',
    description: 'Label of transaction category PROVISION_PREVENTIVE_SEIZURE',
  },
  [TransactionBankOperationCategory.REFUND_ACCOUNT_SEIZURE_FEES]: {
    id: 'transaction.category.REFUND_ACCOUNT_SEIZURE_FEES',
    defaultMessage: 'Refund account seizure fees',
    description: 'Label of transaction category REFUND_ACCOUNT_SEIZURE_FEES',
  },
  [TransactionBankOperationCategory.REFUND_BANK_INCIDENT_FEES]: {
    id: 'transaction.category.REFUND_BANK_INCIDENT_FEES',
    defaultMessage: 'Bank incident refund fees',
    description: 'Label of transaction category REFUND_BANK_INCIDENT_FEES',
  },
  [TransactionBankOperationCategory.CREDIT_CARD_RESET]: {
    id: 'transaction.category.CREDIT_CARD_RESET',
    defaultMessage: 'Credit card reset',
    description: 'Label of transaction category CREDIT_CARD_RESET',
  },
  [TransactionBankOperationCategory.REFUND_BANK_SERVICE_FEES]: {
    id: 'transaction.category.REFUND_BANK_SERVICE_FEES',
    defaultMessage: 'Bank service refund fees',
    description: 'Label of transaction category REFUND_BANK_SERVICE_FEES',
  },
  [TransactionBankOperationCategory.REFUND_DIRECT_DEBT_RECOVERY_FEES]: {
    id: 'transaction.category.REFUND_DIRECT_DEBT_RECOVERY_FEES',
    defaultMessage: 'Direct debt recovery fees refund',
    description: 'Label of transaction category REFUND_DIRECT_DEBT_RECOVERY_FEES',
  },
  [TransactionBankOperationCategory.REFUND_PREVENTIVE_SEIZURE_FEES]: {
    id: 'transaction.category.REFUND_PREVENTIVE_SEIZURE_FEES',
    defaultMessage: 'Preventive seizure fees refund',
    description: 'Label of transaction category REFUND_PREVENTIVE_SEIZURE_FEES',
  },
  [TransactionBankOperationCategory.REJECTION_CHECK]: {
    id: 'transaction.category.REJECTION_CHECK',
    defaultMessage: 'Check rejection',
    description: 'Label of transaction category REJECTION_CHECK',
  },
  [TransactionBankOperationCategory.REJECTION_LOAN_REPAYMENT]: {
    id: 'transaction.category.REJECTION_LOAN_REPAYMENT',
    defaultMessage: 'Loan repayment rejection',
    description: 'Label of transaction category REJECTION_LOAN_REPAYMENT',
  },
  [TransactionBankOperationCategory.REJECTION_PAYMENT]: {
    id: 'transaction.category.REJECTION_PAYMENT',
    defaultMessage: 'Payment rejection',
    description: 'Label of transaction category REJECTION_PAYMENT',
  },
  [TransactionBankOperationCategory.RELEASE_ACCOUNT_SEIZURE]: {
    id: 'transaction.category.RELEASE_ACCOUNT_SEIZURE',
    defaultMessage: 'Account seizure release',
    description: 'Label of transaction category RELEASE_ACCOUNT_SEIZURE',
  },
  [TransactionBankOperationCategory.RELEASE_DIRECT_DEBT_RECOVERY]: {
    id: 'transaction.category.RELEASE_DIRECT_DEBT_RECOVERY',
    defaultMessage: 'Direct debt recovery release',
    description: 'Label of transaction category RELEASE_DIRECT_DEBT_RECOVERY',
  },
  [TransactionBankOperationCategory.RELEASE_PREVENTIVE_SEIZURE]: {
    id: 'transaction.category.RELEASE_PREVENTIVE_SEIZURE',
    defaultMessage: 'Preventive seizure release',
    description: 'Label of transaction category RELEASE_PREVENTIVE_SEIZURE',
  },
  [TransactionCreditCategory.BAILIFF]: {
    id: 'transaction.category.BAILIFF',
    defaultMessage: 'Bailiff',
    description: 'Label of transaction category BAILIFF',
  },
  [TransactionCreditCategory.LEASING]: {
    id: 'transaction.category.LEASING',
    defaultMessage: 'Leasing',
    description: 'Label of transaction category LEASING',
  },
  [TransactionCreditCategory.LOAN_DRAWDOWN]: {
    id: 'transaction.category.LOAN_DRAWDOWN',
    defaultMessage: 'Loan drawdown',
    description: 'Label of transaction category LOAN_DRAWDOWN',
  },
  [TransactionCreditCategory.LOAN_DRAWDOWN_PERSONAL]: {
    id: 'transaction.category.LOAN_DRAWDOWN_PERSONAL',
    defaultMessage: 'Personal loan drawdown',
    description: 'Label of transaction category LOAN_DRAWDOWN_PERSONAL',
  },
  [TransactionCreditCategory.LOAN_DRAWDOWN_REAL_ESTATE]: {
    id: 'transaction.category.LOAN_DRAWDOWN_REAL_ESTATE',
    defaultMessage: 'Real estate loan drawdown',
    description: 'Label of transaction category LOAN_DRAWDOWN_REAL_ESTATE',
  },
  [TransactionCreditCategory.LOAN_DRAWDOWN_REVOLVING]: {
    id: 'transaction.category.LOAN_DRAWDOWN_REVOLVING',
    defaultMessage: 'Revolving loan drawdown',
    description: 'Label of transaction category LOAN_DRAWDOWN_REVOLVING',
  },
  [TransactionCreditCategory.LOAN_DRAWDOWN_FAST]: {
    id: 'transaction.category.LOAN_DRAWDOWN_FAST',
    defaultMessage: 'Fast loan drawdown',
    description: 'Label of transaction category LOAN_DRAWDOWN_FAST',
  },
  [TransactionCreditCategory.LOAN_DRAWDOWN_STUDENT]: {
    id: 'transaction.category.LOAN_DRAWDOWN_STUDENT',
    defaultMessage: 'Student loan drawdown',
    description: 'Label of transaction category LOAN_DRAWDOWN_STUDENT',
  },
  [TransactionCreditCategory.LOAN_PREPAYMENT]: {
    id: 'transaction.category.LOAN_PREPAYMENT',
    defaultMessage: 'Loan prepayment',
    description: 'Label of transaction category LOAN_PREPAYMENT',
  },
  [TransactionCreditCategory.LOAN_REPAYMENT]: {
    id: 'transaction.category.LOAN_REPAYMENT',
    defaultMessage: 'Loan repayment',
    description: 'Label of transaction category LOAN_REPAYMENT',
  },
  [TransactionCreditCategory.LOAN_REPAYMENT_PERSONAL]: {
    id: 'transaction.category.LOAN_REPAYMENT_PERSONAL',
    defaultMessage: 'Personal loan repayment',
    description: 'Label of transaction category LOAN_REPAYMENT_PERSONAL',
  },
  [TransactionCreditCategory.LOAN_REPAYMENT_REAL_ESTATE]: {
    id: 'transaction.category.LOAN_REPAYMENT_REAL_ESTATE',
    defaultMessage: 'Real estate loan repayment',
    description: 'Label of transaction category LOAN_REPAYMENT_REAL_ESTATE',
  },
  [TransactionCreditCategory.LOAN_REPAYMENT_REVOLVING]: {
    id: 'transaction.category.LOAN_REPAYMENT_REVOLVING',
    defaultMessage: 'Revolving loan repayment',
    description: 'Label of transaction category LOAN_REPAYMENT_REVOLVING',
  },
  [TransactionCreditCategory.LOAN_REPAYMENT_FAST]: {
    id: 'transaction.category.LOAN_REPAYMENT_FAST',
    defaultMessage: 'Fast loan',
    description: 'Label of transaction category LOAN_REPAYMENT_FAST',
  },
  [TransactionCreditCategory.LOAN_REPAYMENT_STUDENT]: {
    id: 'transaction.category.LOAN_REPAYMENT_STUDENT',
    defaultMessage: 'Student loan',
    description: 'Label of transaction category LOAN_REPAYMENT_STUDENT',
  },

  [TransactionCreditCategory.SPLIT_PAYMENT]: {
    id: 'transaction.category.SPLIT_PAYMENT',
    defaultMessage: 'Split payment',
    description: 'Label of transaction category SPLIT_PAYMENT',
  },
  [TransactionCreditCategory.DEBT_COLLECTION]: {
    id: 'transaction.category.DEBT_COLLECTION',
    defaultMessage: 'Debt collection',
    description: 'Label of transaction category DEBT_COLLECTION',
  },
  [TransactionExpenseCategory.PAID_ALIMONY]: {
    id: 'transaction.category.PAID_ALIMONY',
    defaultMessage: 'Paid alimony',
    description: 'Label of transaction category PAID_ALIMONY',
  },
  [TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT]: {
    id: 'transaction.category.DEFERRED_TOTAL_PAYMENT',
    defaultMessage: 'Deferred card deferred',
    description: 'Label of transaction category DEFERRED_TOTAL_PAYMENT',
  },
  [TransactionExpenseCategory.GAMBLING]: {
    id: 'transaction.category.GAMBLING',
    defaultMessage: 'Gambling',
    description: 'Label of transaction category GAMBLING',
  },
  [TransactionExpenseCategory.GROCERY]: {
    id: 'transaction.category.GROCERY',
    defaultMessage: 'Grocery',
    description: 'Label of transaction category GROCERY',
  },
  [TransactionExpenseCategory.HEALTH]: {
    id: 'transaction.category.HEALTH',
    defaultMessage: 'Health',
    description: 'Label of transaction category HEALTH',
  },
  [TransactionExpenseCategory.INSURANCE]: {
    id: 'transaction.category.INSURANCE',
    defaultMessage: 'Insurance',
    description: 'Label of transaction category INSURANCE',
  },
  [TransactionExpenseCategory.INSURANCE_AUTO]: {
    id: 'transaction.category.INSURANCE_AUTO',
    defaultMessage: 'Insurance',
    description: 'Label of transaction category INSURANCE_AUTO',
  },
  [TransactionExpenseCategory.INSURANCE_HEALTH]: {
    id: 'transaction.category.INSURANCE_HEALTH',
    defaultMessage: 'Health insurance',
    description: 'Label of transaction category INSURANCE_HEALTH',
  },
  [TransactionExpenseCategory.INSURANCE_HOME]: {
    id: 'transaction.category.INSURANCE_HOME',
    defaultMessage: 'Home insurance',
    description: 'Label of transaction category INSURANCE_HOME',
  },
  [TransactionExpenseCategory.INSURANCE_LOAN]: {
    id: 'transaction.category.INSURANCE_LOAN',
    defaultMessage: 'Loan insurance',
    description: 'Label of transaction category INSURANCE_LOAN',
  },
  [TransactionExpenseCategory.LEISURE]: {
    id: 'transaction.category.LEISURE',
    defaultMessage: 'Leisure',
    description: 'Label of transaction category LEISURE',
  },
  [TransactionExpenseCategory.MULTIMEDIA]: {
    id: 'transaction.category.MULTIMEDIA',
    defaultMessage: 'Multimedia',
    description: 'Label of transaction category MULTIMEDIA',
  },
  [TransactionExpenseCategory.OTHER_EXPENSE]: {
    id: 'transaction.category.OTHER_EXPENSE',
    defaultMessage: 'Other expense',
    description: 'Label of transaction category OTHER_EXPENSE',
  },
  [TransactionExpenseCategory.POWER]: {
    id: 'transaction.category.POWER',
    defaultMessage: 'Power',
    description: 'Label of transaction category POWER',
  },
  [TransactionExpenseCategory.RENT]: {
    id: 'transaction.category.RENT',
    defaultMessage: 'Rent',
    description: 'Label of transaction category RENT',
  },
  [TransactionExpenseCategory.SHOPPING]: {
    id: 'transaction.category.SHOPPING',
    defaultMessage: 'Shopping',
    description: 'Label of transaction category SHOPPING',
  },
  [TransactionExpenseCategory.SPORT]: {
    id: 'transaction.category.SPORT',
    defaultMessage: 'Sport / Gym',
    description: 'Label of transaction category SPORT',
  },
  [TransactionExpenseCategory.TELECOM]: {
    id: 'transaction.category.TELECOM',
    defaultMessage: 'Telecom',
    description: 'Label of transaction category TELECOM',
  },
  [TransactionExpenseCategory.TOBACCO]: {
    id: 'transaction.category.TOBACCO',
    defaultMessage: 'Tobacco',
    description: 'Label of transaction category TOBACCO',
  },
  [TransactionExpenseCategory.TRANSPORT]: {
    id: 'transaction.category.TRANSPORT',
    defaultMessage: 'Transport',
    description: 'Label of transaction category TRANSPORT',
  },
  [TransactionExpenseCategory.REGULAR_EXPENSE]: {
    id: 'transaction.category.REGULAR_EXPENSE',
    defaultMessage: 'Regular expense',
    description: 'Label of transaction category REGULAR_EXPENSE',
  },
  [TransactionExpenseCategory.ATM]: {
    id: 'transaction.category.ATM',
    defaultMessage: 'Cash withdrawal',
    description: 'Label of transaction category ATM',
  },
  [TransactionExpenseCategory.CARD_PAYMENT]: {
    id: 'transaction.category.CARD_PAYMENT',
    defaultMessage: 'Card payment',
    description: 'Label of transaction category CARD_PAYMENT',
  },
  [TransactionExpenseCategory.CHECK]: {
    id: 'transaction.category.CHECK',
    defaultMessage: 'Check',
    description: 'Label of transaction category CHECK',
  },
  [TransactionIncomeCategory.ALLOWANCE]: {
    id: 'transaction.category.ALLOWANCE',
    defaultMessage: 'Allowance',
    description: 'Label of transaction category ALLOWANCE',
  },
  [TransactionIncomeCategory.ALLOWANCE_FAMILY]: {
    id: 'transaction.category.ALLOWANCE_FAMILY',
    defaultMessage: 'Family allowance',
    description: 'Label of transaction category ALLOWANCE_FAMILY',
  },
  [TransactionIncomeCategory.ALLOWANCE_HEALTH]: {
    id: 'transaction.category.ALLOWANCE_HEALTH',
    defaultMessage: 'Health allowance',
    description: 'Label of transaction category ALLOWANCE_HEALTH',
  },
  [TransactionIncomeCategory.ALLOWANCE_DISABILITY]: {
    id: 'transaction.category.ALLOWANCE_DISABILITY',
    defaultMessage: 'Disability allowance',
    description: 'Label of transaction category ALLOWANCE_DISABILITY',
  },
  [TransactionIncomeCategory.ALLOWANCE_HOUSING]: {
    id: 'transaction.category.ALLOWANCE_HOUSING',
    defaultMessage: 'Housing allowance',
    description: 'Label of transaction category ALLOWANCE_HOUSING',
  },
  [TransactionIncomeCategory.ALLOWANCE_INCOME_SUPPORT]: {
    id: 'transaction.category.ALLOWANCE_INCOME_SUPPORT',
    defaultMessage: 'Allowance income support',
    description: 'Label of transaction category ALLOWANCE_INCOME_SUPPORT',
  },
  [TransactionIncomeCategory.ALLOWANCE_UNEMPLOYMENT]: {
    id: 'transaction.category.ALLOWANCE_UNEMPLOYMENT',
    defaultMessage: 'Unemployment allowance',
    description: 'Label of transaction category ALLOWANCE_UNEMPLOYMENT',
  },
  [TransactionIncomeCategory.DISSAVING]: {
    id: 'transaction.category.DISSAVING',
    defaultMessage: 'Dissaving',
    description: 'Label of transaction category DISSAVING',
  },
  [TransactionIncomeCategory.GAMBLING_GAIN]: {
    id: 'transaction.category.GAMBLING_GAIN',
    defaultMessage: 'Gambling gain',
    description: 'Label of transaction category GAMBLING_GAIN',
  },
  [TransactionIncomeCategory.OTHER_ENTRY]: {
    id: 'transaction.category.OTHER_ENTRY',
    defaultMessage: 'Other entry',
    description: 'Label of transaction category OTHER_ENTRY',
  },
  [TransactionIncomeCategory.RECEIVED_ALIMONY]: {
    id: 'transaction.category.RECEIVED_ALIMONY',
    defaultMessage: 'Received alimony',
    description: 'Label of transaction category RECEIVED_ALIMONY',
  },
  [TransactionIncomeCategory.RENTAL_INCOME]: {
    id: 'transaction.category.RENTAL_INCOME',
    defaultMessage: 'Rental income',
    description: 'Label of transaction category RENTAL_INCOME',
  },
  [TransactionIncomeCategory.RETIREMENT_PENSION]: {
    id: 'transaction.category.RETIREMENT_PENSION',
    defaultMessage: 'Retirement pension',
    description: 'Label of transaction category RETIREMENT_PENSION',
  },
  [TransactionIncomeCategory.REVERSIONARY_PENSION]: {
    id: 'transaction.category.REVERSIONARY_PENSION',
    defaultMessage: 'Reversionary pension',
    description: 'Label of transaction category REVERSIONARY_PENSION',
  },
  [TransactionIncomeCategory.STATE_SUBSIDY]: {
    id: 'transaction.category.STATE_SUBSIDY',
    defaultMessage: 'State subsidy',
    description: 'Label of transaction category STATE_SUBSIDY',
  },
  [TransactionIncomeCategory.WAGE]: {
    id: 'transaction.category.WAGE',
    defaultMessage: 'Wage',
    description: 'Label of transaction category WAGE',
  },
  [TransactionIncomeCategory.WAGE_ADVANCE_PAYMENT]: {
    id: 'transaction.category.WAGE_ADVANCE_PAYMENT',
    defaultMessage: 'Wage advance payment',
    description: 'Label of transaction category WAGE_ADVANCE_PAYMENT',
  },
  [TransactionIncomeCategory.WAGE_BONUS]: {
    id: 'transaction.category.WAGE_BONUS',
    defaultMessage: 'Wage bonus',
    description: 'Label of transaction category WAGE_BONUS',
  },
  [TransactionIncomeCategory.WAGE_SALARY_ADVANCE]: {
    id: 'transaction.category.WAGE_SALARY_ADVANCE',
    defaultMessage: 'Wage salary advance',
    description: 'Label of transaction category WAGE_SALARY_ADVANCE',
  },
  [TransactionIncomeCategory.FREELANCE]: {
    id: 'transaction.category.FREELANCE',
    defaultMessage: 'Earnings from self-employment',
    description: 'Label of transaction category FREELANCE',
  },
  [TransactionIncomeCategory.REGULAR_INCOME]: {
    id: 'transaction.category.REGULAR_INCOME',
    defaultMessage: 'Regular income',
    description: 'Label of transaction category REGULAR_INCOME',
  },
  [TransactionRefundCategory.REFUND]: {
    id: 'transaction.category.REFUND',
    defaultMessage: 'Refund',
    description: 'Label of transaction category REFUND',
  },
  [TransactionRefundCategory.REFUND_EXPENSE_REPORT]: {
    id: 'transaction.category.REFUND_EXPENSE_REPORT',
    defaultMessage: 'Refund expense report',
    description: 'Label of transaction category REFUND_EXPENSE_REPORT',
  },
  [TransactionRefundCategory.REFUND_INSURANCE]: {
    id: 'transaction.category.REFUND_INSURANCE',
    defaultMessage: 'Refund insurance',
    description: 'Label of transaction category REFUND_INSURANCE',
  },
  [TransactionSavingCategory.BANKBOOK_SAVING]: {
    id: 'transaction.category.BANKBOOK_SAVING',
    defaultMessage: 'Bankbook saving',
    description: 'Label of transaction category BANKBOOK_SAVING',
  },
  [TransactionSavingCategory.INTEREST]: {
    id: 'transaction.category.INTEREST',
    defaultMessage: 'Interest',
    description: 'Label of transaction category INTEREST',
  },
  [TransactionSavingCategory.SAVING]: {
    id: 'transaction.category.SAVING',
    defaultMessage: 'Saving',
    description: 'Label of transaction category SAVING',
  },
  [TransactionSavingCategory.SHARE_SAVING]: {
    id: 'transaction.category.SHARE_SAVING',
    defaultMessage: 'Share saving',
    description: 'Label of transaction category SHARE_SAVING',
  },
});
